<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Financial Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Unpaid Runs</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Unpaid Runs</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewUnpaidRun" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Unpaid Run</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="unpaidRuns"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'startDate', 'endDate']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on ID, Start Date or End Date"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No unpaid runs found.
                        </template>

                        <template #loading>
                            Loading unpaid runs. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Initiated</span>
                                    <span v-if="data.status === 'P'"
                                          :class="'status-badge status-' + data.status">Paid</span>
                                    <span v-if="data.status === 'C'"
                                          :class="'status-badge status-' + data.status">Completed</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>


                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Unpaid Run</h3>

                    <div class="field">
                        <label for="startDate">Start Date</label>
                        <Calendar id="startDate" v-model="newUnpaidRun.startDate" required="true"
                                  :showTime="true"
                                  :showSeconds="true" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="endDate">End Date</label>
                        <Calendar id="endDate" v-model="newUnpaidRun.endDate" required="true"
                                  :showTime="true"
                                  :showSeconds="true" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createUnpaidRun"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Invoices for Unpaid Run from {{ this.unpaidRun.startDate }} to {{
                            this.unpaidRun.endDate
                        }}</h3>

                    <DataTable
                        :value="invoices"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        responsiveLayout="scroll">

                        <template #empty>
                            No invoices found.
                        </template>

                        <template #loading>
                            Loading invoices. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 5%">
                            <template #body="{ data }">
                                <Button v-tooltip="'View'" icon="pi pi-eye"
                                        class="p-button-rounded p-button-help m-1"
                                        @click="callViewInvoice(data)"/>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Unpaid</span>
                                <span v-if="data.status === 'S'"
                                      :class="'status-badge status-' + data.status">Submitted</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Paid</span>
                                <span v-if="data.status === 'U'"
                                      :class="'status-badge status-' + data.status">Unpaids Process</span>

                            </template>
                        </Column>

                        <Column field="id" header="Invoice #" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Invoice #</span>
                                {{ data.uniqueId }}
                            </template>
                        </Column>

                        <Column field="invoiced" header="Invoiced" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Invoiced</span>
                                {{ data.invoiced }}
                            </template>
                        </Column>

                        <Column field="appointmentDate" header="Appointment Date" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Appointment Date</span>
                                {{ data.appointmentDate }}
                            </template>
                        </Column>

                        <Column field="amount" header="Amount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Amount</span>
                                {{ format_amount(data.amount) }}
                            </template>
                        </Column>

                        <Column field="practitionerName" header="Practitioner Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Practitioner Name</span>
                                {{ data.practitionerName }}
                            </template>
                        </Column>

                        <Column field="clientName" header="Client Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client Name</span>
                                {{ data.clientName }}
                            </template>
                        </Column>

                        <Column field="billingPlanName" header="Billing Plan" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Billing Plan</span>
                                {{ data.billingPlanName }}
                            </template>
                        </Column>

                    </DataTable>

                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Unpaid Run from {{ this.unpaidRun.startDate }} to {{ this.unpaidRun.endDate }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Unpaid Run?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteUnpaidRun"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Clients of Unpaid Run from {{ this.unpaidRun.startDate }} to {{ this.unpaidRun.endDate }}</h3>

                    <DataTable
                        :value="unpaidAccounts"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        responsiveLayout="scroll">

                        <template #empty>
                            No unpaid accounts found.
                        </template>

                        <template #loading>
                            Loading unpaid accounts. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItemsUnpaidAccounts(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <Column field="id" header="Client #" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client #</span>
                                {{ data.clientId }}
                            </template>
                        </Column>

                        <Column field="id" header="Client Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client Name</span>
                                {{ data.clientName }}
                            </template>
                        </Column>

                        <Column field="id" header="Client Email" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client Email</span>
                                {{ data.clientEmail }}
                            </template>
                        </Column>

                        <Column field="id" header="Client Mobile" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Client Mobile</span>
                                {{ data.clientMobile }}
                            </template>
                        </Column>

                        <Column field="invoiceAmount" header="Invoice Amount" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Invoice Amount</span>
                                {{ format_amount(data.amount) }}
                            </template>
                        </Column>

                        <Column field="amountDue" header="Amount Due" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Amount Due</span>
                                {{ format_amount(data.amountDue) }}
                            </template>
                        </Column>

                    </DataTable>

                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>
                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Send Email to Client {{ this.unpaidAccount.clientName }}</h3>

                    <div class="field">
                        <label for="emailSubject">Email Subject</label>
                        <InputText id="emailSubject"
                                   v-model.trim="email.emailSubject"
                                   required="true"/>
                    </div>

                    <div class="field">
                        <label for="emailMessage">Email Message</label>
                        <Editor v-model="email.emailMessage" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                                    <span class="ql-formats">
                                                        <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                                        <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                                        <button class="ql-underline"
                                                                v-tooltip.bottom="'Underline'"></button>
                                                        <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                                        <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                                        <button class="ql-background"
                                                                v-tooltip.bottom="'Color'"></button>
                                                        <button class="ql-list" value="bullet"
                                                                v-tooltip.bottom="'Bulleted List'"></button>
                                                        <button class="ql-list" value="ordered"
                                                                v-tooltip.bottom="'Ordered List'"></button>
                                                        <button class="ql-indent" value="-1"
                                                                v-tooltip.bottom="'Indent -1'"></button>
                                                        <button class="ql-indent" value="+1"
                                                                v-tooltip.bottom="'Indent +1'"></button>
                                                    </span>
                            </template>
                        </Editor>
                    </div>

                    <Button label="Send" class="mr-2 mb-2" @click="sendEmail"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callResetUnpaidAccounts"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Send SMS to Client {{ this.unpaidAccount.clientName }}</h3>

                    <div class="field">
                        <label for="smsMessage">SMS Message</label>
                        <InputText id="smsMessage"
                                   v-model.trim="sms.smsMessage"
                                   required="true"/>
                    </div>

                    <Button label="Send" class="mr-2 mb-2" @click="sendSms"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callResetUnpaidAccounts"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 9" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Add Audit Trail Entry for Client {{ this.unpaidAccount.clientName }}</h3>

                    <div class="field">
                        <label for="auditTrailSubProcess">Audit Trail Sub Process</label>
                        <Dropdown v-model="auditTrail.auditTrailSubProcess" :options="subProcessTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="auditTrailDescription">Audit Trail Description</label>
                        <Textarea v-model="auditTrail.auditTrailDescription" :autoResize="true"
                                  rows="10" cols="50"/>
                    </div>

                    <Button label="Add" class="mr-2 mb-2" @click="addAuditTrail"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callResetUnpaidAccounts"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 10" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Send Unpaid Notice to Client {{ this.unpaidAccount.clientName }}</h3>

                    <div class="field">
                        <label for="unpaidType">Type</label>
                        <Dropdown v-model="unpaid.unpaidType" :options="unpaidTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <div class="field">
                        <label for="unpaidAmountOutstanding">Amount Outstanding</label>
                        <InputText id="unpaidAmountOutstanding"
                                   v-model.trim="unpaid.unpaidAmountOutstanding"
                                   required="true"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createUnpaidNotice"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callResetUnpaidAccounts"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import UnpaidRunService from "@/service/UnpaidRunService";
import moment from "moment";
import InvoiceService from "@/service/InvoiceService";
import ClientService from "@/service/ClientService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

let numeral = require('numeral');

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'status', header: 'Status', sortable: true},
            {field: 'startDate', header: 'Start Date', sortable: true},
            {field: 'endDate', header: 'End Date', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'status', header: 'Status', sortable: true},
            {field: 'startDate', header: 'Start Date', sortable: true},
            {field: 'endDate', header: 'End Date', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'unpaid', header: 'Unpaid'},
            {field: 'paid', header: 'Paid'},
            {field: 'completed', header: 'Completed'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            filteredItemsUnpaidAccountsCache: new Map(),
            unpaidRun: {},
            unpaidRuns: null,
            originalUnpaidRuns: null,
            newUnpaidRun: {},
            invoices: null,
            unpaidAccounts: null,
            unpaidAccount: {},
            unpaid: {},
            auditTrail: {},
            email: {},
            sms: {},
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            subProcessTypes: [
                {name: 'Unpaids Management', code: 'Unpaids Management'},
                {name: 'Client Management', code: 'Client Management'},
                {name: 'Billing Management', code: 'Billing Management'}
            ],
            unpaidTypes: [
                {name: 'Member Liable', code: 'M'},
                {name: 'Gentle Notice', code: 'G'},
                {name: 'First Notice', code: 'F'},
                {name: 'Final Notice', code: 'S'},
                {name: 'Litigation Notice', code: 'L'},
                {name: 'Account Internally Written Off', code: 'C'}
            ]
        }
    },
    watch: {
        unpaidRuns() {
            this.filteredItemsCache.clear();
        },
        unpaidAccounts() {
            this.filteredItemsUnpaidAccountsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[PhoneCalls][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [unpaidRunsData, companyConfigData] = await Promise.all([
                    UnpaidRunService.getCompanyUnpaidRuns(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.unpaidRuns = unpaidRunsData.data;
                this.originalUnpaidRuns = unpaidRunsData.data;
                console.log("[PhoneCalls][mounted] unpaidRunsData.data = ", unpaidRunsData.data);

                this.companyConfiguration = companyConfigData.data;
                console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.unpaidRuns = ", this.unpaidRuns);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'startDate': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'endDate': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callResetUnpaidAccounts() {
            this.activeTab = 6;
        },
        format_amount(value) {
            if (value) {
                return numeral(String(value )).format('0,0.00');
            }
        },
        convertDateIntoUtcTimezone(inputDate) {
            console.log("[convertDateIntoUtcTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");

                let inputTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    inputTimeZone = "Africa/Johannesburg";
                }

                const targetTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                console.log("[convertDateIntoUtcTimezone] inputTimeZone :", inputTimeZone);
                console.log("[convertDateIntoUtcTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoUtcTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoUtcTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        convertDateIntoLocalTimezone(inputDate) {
            console.log("[convertDateIntoLocalTimezone] inputDate :", inputDate);

            try {
                const moment = require("moment-timezone");
                const inputTimeZone = "Etc/UTC"; // Assuming the input date is in UTC

                let targetTimeZone = "Europe/London";

                if (this.companyConfiguration.region == "ZA") {
                    targetTimeZone = "Africa/Johannesburg";
                }

                console.log("[convertDateIntoLocalTimezone] targetTimeZone :", targetTimeZone);

                // Parse the input date string and set the input time zone
                const parsedDate = moment.tz(inputDate, "YYYY-MM-DD HH:mm:ss", inputTimeZone);

                console.log("[convertDateIntoLocalTimezone] parsedDate :", parsedDate);

                // Convert the date to the target time zone
                const outputDate = parsedDate.clone().tz(targetTimeZone);

                console.log("[convertDateIntoLocalTimezone] Output Date :", outputDate.format("YYYY-MM-DD HH:mm:ss"));

                return outputDate.format("YYYY-MM-DD HH:mm:ss");

            } catch (error) {
                console.error("[convertDateIntoLocalTimezone] Could not convert date. Returning input date. Error :", error);

                return inputDate;
            }

        },
        callNewUnpaidRun() {
            const currentDate = moment();
            const previousDate = currentDate.clone().subtract(1, 'months');

            console.log("[UnpaidRuns][callNewUnpaidRun] currentDate = ", currentDate);
            console.log("[UnpaidRuns][callNewUnpaidRun] previousDate = ", previousDate);

            this.newUnpaidRun.startDate = previousDate.format('YYYY-MM-DD HH:mm:ss');
            this.newUnpaidRun.endDate = currentDate.format('YYYY-MM-DD HH:mm:ss');

            this.newUnpaidRun = {
                startDate: previousDate.format('YYYY-MM-DD HH:mm:ss'),
                endDate: currentDate.format('YYYY-MM-DD HH:mm:ss')
            };

            this.activeTab = 2;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        async callViewInvoices(data) {
            this.activeTab = 3;
            this.unpaidRun = data;

            try {
                const [resultsData] = await Promise.all([
                    InvoiceService.getCompanyInvoicesForUnpaidRun(this.profile.companyId, this.unpaidRun.id)
                ]);

                this.invoices = resultsData.data;
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find invoices. Error = ", e);
            }

        },
        callViewInvoice(data) {
            console.log('[UnpaidRuns][callViewInvoice] data = ', data);
            this.$router.push({name: 'commonFinancialInvoices', query: {inputInvoiceId: data.id}});
        },
        async callManageUnpaidRun(data) {
            this.activeTab = 6;
            this.unpaidRun = data;

            try {
                const [resultsData] = await Promise.all([
                    InvoiceService.getInvoiceInfoForUnpaidRun(this.profile.companyId, this.unpaidRun.id)
                ]);

                this.unpaidAccounts = resultsData.data;
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find invoices. Error = ", e);
            }

        },
        callDeleteUnpaidRun(data) {
            this.activeTab = 5;
            this.confirmationResponse = 'N';
            this.unpaidRun = data;
        },
        callSendEmail(data) {
            this.activeTab = 7;
            this.unpaidAccount = data;
            this.email = {};
        },
        callSendSms(data) {
            this.activeTab = 8;
            this.unpaidAccount = data;
            this.sms = {};
        },
        callAddAuditTrail(data) {
            this.activeTab = 9;
            this.unpaidAccount = data;
            this.auditTrail = {};
        },
        callCreateUnpaidNotice(data) {
            this.activeTab = 10;
            this.unpaidAccount = data;
            this.unpaid = {};
            this.unpaid.unpaidType = 'G';
            this.unpaid.unpaidAmountOutstanding = this.unpaidAccount.amountDue;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isUnpaid = value.some(item => item.field === "unpaid");
            const isPaid = value.some(item => item.field === "paid");
            const isCompleted = value.some(item => item.field === "completed");

            if (this.unpaidRuns !== null) {

                if (isUnpaid) {
                    this.unpaidRuns = this.originalUnpaidRuns.filter(unpaidRun => unpaidRun.status === 'I');
                } else if (isPaid) {
                    this.unpaidRuns = this.originalUnpaidRuns.filter(unpaidRun => unpaidRun.status === 'P');
                } else if (isCompleted) {
                    this.unpaidRuns = this.originalUnpaidRuns.filter(unpaidRun => unpaidRun.status === 'C');
                } else {
                    this.unpaidRuns = this.originalUnpaidRuns;
                }

            } else {
                this.unpaidRuns = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'View Invoices',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewInvoices(data);
                }
            });

            items.push({
                label: 'Manage',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callManageUnpaidRun(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteUnpaidRun(data);
                }
            });

            console.log('[PhoneCalls][getFilteredItems] items = ', items);

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        getFilteredItemsUnpaidAccounts(data) {
            if (this.filteredItemsUnpaidAccountsCache.has(data.id)) {
                return this.filteredItemsUnpaidAccountsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Send Email',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callSendEmail(data);
                }
            });

            items.push({
                label: 'Send SMS',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callSendSms(data);
                }
            });

            items.push({
                label: 'Add Audit Trail',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callAddAuditTrail(data);
                }
            });

            items.push({
                label: 'Create Unpaid Notice',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callCreateUnpaidNotice(data);
                }
            });

            console.log('[PhoneCalls][getFilteredItems] items = ', items);

            this.filteredItemsUnpaidAccountsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async createUnpaidRun() {

            this.submitted = true;

            if (this.newUnpaidRun.startDate) {

                try {
                    const startDateInput = new Date(this.convertDateIntoUtcTimezone(this.newUnpaidRun.startDate));
                    const yearStart = startDateInput.getFullYear();
                    const monthStart = String(startDateInput.getMonth() + 1).padStart(2, '0');
                    const dayStart = String(startDateInput.getDate()).padStart(2, '0');
                    const hoursStart = String(startDateInput.getHours()).padStart(2, '0');
                    const minutesStart = String(startDateInput.getMinutes()).padStart(2, '0');
                    const secondsStart = String(startDateInput.getSeconds()).padStart(2, '0');

                    const startDate = `${yearStart}-${monthStart}-${dayStart} ${hoursStart}:${minutesStart}:${secondsStart}`;

                    const endDateInput = new Date(this.convertDateIntoUtcTimezone(this.newUnpaidRun.endDate));
                    const yearEnd = endDateInput.getFullYear();
                    const monthEnd = String(endDateInput.getMonth() + 1).padStart(2, '0');
                    const dayEnd = String(endDateInput.getDate()).padStart(2, '0');
                    const hoursEnd = String(endDateInput.getHours()).padStart(2, '0');
                    const minutesEnd = String(endDateInput.getMinutes()).padStart(2, '0');
                    const secondsEnd = String(endDateInput.getSeconds()).padStart(2, '0');

                    const endDate = `${yearEnd}-${monthEnd}-${dayEnd} ${hoursEnd}:${minutesEnd}:${secondsEnd}`;

                    console.log("[" + name + "][saveUnpaidRun] this.newUnpaidRun.startDate = ", this.newUnpaidRun.startDate);
                    console.log("[" + name + "][saveUnpaidRun] startDate = ", startDate);

                    console.log("[" + name + "][saveUnpaidRun] this.newUnpaidRun.endDate = ", this.newUnpaidRun.endDate);
                    console.log("[" + name + "][saveUnpaidRun] endDate = ", endDate);

                    var newUnpaidRunDto = {
                        startDate: startDate,
                        endDate: endDate,
                        status: 'I'
                    };

                    console.log("[" + name + "][saveUnpaidRun] newUnpaidRunDto = ", newUnpaidRunDto);

                    const response = await UnpaidRunService.createUnpaidRun(this.profile.companyId, newUnpaidRunDto)
                    console.log('[PhoneCalls][updatePhoneCall] Response = ', response);

                    const [resultsData] = await Promise.all([
                        UnpaidRunService.getCompanyUnpaidRuns(this.profile.companyId)
                    ]);

                    this.unpaidRuns = resultsData.data;
                    this.originalUnpaidRuns = resultsData.data;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created unpaid run',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[PhoneCalls][updatePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create unpaid run. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newUnpaidRun = {};
            this.activeTab = 1;
            this.submitted = false;

        },
        async deleteUnpaidRun() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.unpaidRun.id) {

                    try {
                        const response = await UnpaidRunService.deleteUnpaidRun(this.unpaidRun.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [resultsData] = await Promise.all([
                            UnpaidRunService.getCompanyUnpaidRuns(this.profile.companyId)
                        ]);

                        this.unpaidRuns = resultsData.data;
                        this.originalUnpaidRuns = resultsData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted unpaid run',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete unpaid run. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async sendEmail() {
            this.submitted = true;

            console.log("[UnpaidRuns][sendEmail] this.unpaidAccount = ", this.unpaidAccount);

            if (this.unpaidAccount.clientId) {

                try {

                    var emailDto = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        emailSubject: this.email.emailSubject,
                        emailMessage: this.email.emailMessage
                    };

                    console.log("[PmClients][submitToolboxEmail] emailDto = ", emailDto);

                    const response = await ClientService.sendClientEmail(this.unpaidAccount.clientId, emailDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Sent email',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send email. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 6;
            this.submitted = false;

        },
        async sendSms() {
            this.submitted = true;

            if (this.unpaidAccount.clientId) {

                try {

                    var smsDto = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        smsMessage: this.sms.smsMessage,
                    };

                    console.log("[PmClients][submitToolboxSms] smsDto = ", smsDto);

                    const response = await ClientService.sendClientSms(this.unpaidAccount.clientId, smsDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Sent SMS',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to send SMS. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 6;
            this.submitted = false;

        },
        async addAuditTrail() {
            this.submitted = true;

            if (this.unpaidAccount.clientId) {

                try {

                    var auditTrailDto = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        auditTrailSubProcess: this.auditTrail.auditTrailSubProcess,
                        auditTrailDescription: this.auditTrail.auditTrailDescription
                    };

                    console.log("[PmClients][submitToolboxUnpaid] auditTrailDto = ", auditTrailDto);

                    const response = await ClientService.updateClientAuditTrail(this.unpaidAccount.clientId, auditTrailDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Add audit trail for client',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to add audit trail for client. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 6;
            this.submitted = false;

        },
        async createUnpaidNotice() {
            this.submitted = true;

            if (this.unpaidAccount.clientId) {

                try {

                    var unpaidDto = {
                        companyId: this.profile.companyId,
                        userId: this.userStore.user.id,
                        unpaidType: this.unpaid.unpaidType,
                        unpaidAmountOutstanding: this.unpaid.unpaidAmountOutstanding,
                    };

                    console.log("[PmClients][submitToolboxUnpaid] unpaidDto = ", unpaidDto);

                    const response = await ClientService.sendClientUnpaid(this.unpaidAccount.clientId, unpaidDto);
                    console.log('[ClientsNew][saveNewClient] Response = ', response);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Create unpaid notice',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ClientsNew][saveNewClient] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create unpaid notice. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 6;
            this.submitted = false;

        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-I {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-P {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-U {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-F {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-M {
        background: #FFD8B2;
        color: #805B36;
    }

    &.status-H {
        background: #ECCFFF;
        color: #694382;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
